import { useState, useEffect, useRef } from "react";
import {
  Header,
  Modal,
  Button,
  Image,
  Loader,
  Dimmer,
  Form,
  Dropdown,
  Icon,
} from "semantic-ui-react";
import { CREATE_PASS, VALIDATE_PASS } from "./graphQueries";
import { useMutation } from "@apollo/client";
import jwtDecode from "jwt-decode";
import { AuthContext } from "./AuthContext";
import Login from "./Login/Login";
import moment from "moment";
import MenuItems from "./MenuItems/MenuItems";
import Home from "./Home/Home";
import Availability from "./Availability/Availability";
import Users from "./Users/Users";
import Jobs from "./Jobs/Jobs";
import Profile from "./Profile/Profile";
import { formatPhoneNumber } from "./Common/functions";
import Welcome from "./Welcome/Welcome";
import Invite from "./Invite/Invite";

const Dashboard = () => {
  const [loginContext, setLoginContext] = useState({
    userId: "",
    authenticated: false,
    email: "",
    phone: "",
    role: "",
    fullName: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
    dbaName: "",
    firmName: "",
    billingEmail: "",
    cityId: "",
    stateId: "",
    countryId: "",
    connectedAccountId: "",
    preferredReporterId: "",
    liveLitigationLoginId: "",
  });
  const [authenticated, setAuth] = useState(false);
  const [isReady, setReady] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [passcode, setPass] = useState([]);
  const [initializing, setInit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setError] = useState("");
  const [activeMenu, setActiveMenu] = useState("Home");
  const [themeColor, setThemeColor] = useState("#972528");
  const [isRemember, setRemember] = useState(false);
  const [initNewJob, setInitNewJob] = useState(false);
  const [initJobId, setInitJobId] = useState("");
  const [isEmailLogin, setLoginMethod] = useState(true);
  const [isWelcome, setWelcome] = useState(true);

  useEffect(() => {
    // 1. Check for "id" in query string
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const email = urlParams.get("id");

    if (email) {
      // If id is in query, use it, clear old token, and reset login state
      setEmail(email);

      // Remove query string from URL for cleanliness
      const url = new URL(window.location.href);
      url.search = "";
      window.history.replaceState({}, "", url);

      // Clear any stored token and saved login
      localStorage.removeItem("token");
      localStorage.removeItem("stenexusLogin");

      // Make sure no "remember" is set (adjust to your logic)
      setRemember(false);

      // Initialize (optional: pass any special flag to handleInit if needed)
      handleInit();
      return; // Don't run the rest of the logic if id was present!
    }

    // 2. No id in query, so check for token
    const token = localStorage.getItem("token");
    if (token) {
      handleCreateLoginContext(token);
      return; // Stop, no need to restore other login info if token present
    }

    // 3. No id, no token, check for saved login info
    const stored = localStorage.getItem("stenexusLogin");
    if (stored) {
      let pulled = JSON.parse(stored);
      if (pulled.method === "email") {
        setEmail(pulled.value);
        setLoginMethod(true);
      }
      if (pulled.method === "phone") {
        setPhone(pulled.value);
        setLoginMethod(false);
      }
      setRemember(true);
    }

    // 4. If nothing found, just init
    handleInit();
  }, []);

  useEffect(() => {
    if (passcode.length === 6) {
      handleValidatePass();
    }
  }, [passcode]);

  const handleChange = (e, data) => {
    setError("");
    const key = data.id;
    const value = data.value;
    if (key === "email") {
      setEmail(value);
      setPhone("");
    }
    if (key === "phone") {
      const formatted = formatPhoneNumber(value);
      setPhone(formatted);
      setEmail("");
    }
  };

  // const formatPhoneNumber = (value) => {
  //   if (value.length === 0) return "";
  //   if (value.length <= 3) return `(${value}`;
  //   if (value.length <= 6) return `(${value.slice(0, 3)}) ${value.slice(3)}`;
  //   return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
  // };

  useEffect(() => {
    console.log(loginContext);
  }, [loginContext]);

  const handleChangePasscode = (value) => {
    const isNum = /^\d+$/.test(value);
    if (!isNum && value.length > 0) {
      setError("Passcode includes only digits 0-9.");
      return;
    }
    console.log("value", value.length);
    setPass(value);
  };

  const handleCreatePass = () => {
    if (!email && !phone) return;
    if (isRemember) {
      let stored = "";
      if (email) stored = { method: "email", value: email };
      if (phone) stored = { method: "phone", value: phone };
      localStorage.setItem("stenexusLogin", JSON.stringify(stored));
    } else {
      localStorage.removeItem("stenexusLogin");
    }
    setError("");
    setLoading(true);
    if (email) {
      createPass({ variables: { email } });
    } else if (phone) {
      createPass({ variables: { phone: phone.replace(/\D/g, "") } });
    }
  };

  const [createPass] = useMutation(CREATE_PASS, {
    onCompleted: (result) => handleCreatePass_Result(result),
    onError: (error) => handleCreatePass_Error(error),
  });

  const handleCreatePass_Result = (result) => {
    console.log(result);
    if (result.createPass && result.createPass.status === "succeeded") {
      setReady(true);
      setLoading(false);
    }
  };

  const handleCreatePass_Error = (error) => {
    console.log(error.message);
    setLoading(false);
    setError(error.message);
  };

  const handleValidatePass = () => {
    if (passcode.length !== 6) return;
    setError("");
    setLoading(true);
    validatePass({
      variables: { email, phone: phone.replace(/\D/g, ""), passcode },
    });
  };

  const [validatePass] = useMutation(VALIDATE_PASS, {
    onCompleted: (result) => handleValidatePass_Result(result),
    onError: (error) => handleValidatePass_Error(error),
  });

  const handleValidatePass_Result = (result) => {
    console.log(result);
    if (result.validatePass && result.validatePass.token) {
      localStorage.setItem("token", result.validatePass.token);
      handleCreateLoginContext(result.validatePass.token);
      setLoading(false);
      setPass("");
    } else {
      setLoading(false);
      setError(result.validatePass.message);
    }
  };

  const handleValidatePass_Error = (error) => {
    console.log(error);
    setLoading(false);
    setError(error.message);
  };

  const handleCreateLoginContext = (token) => {
    const decoded = jwtDecode(token);
    console.log(decoded);
    console.log(moment.unix(decoded.exp).format("LLL"));
    const isExpired = moment.unix(decoded.exp) < moment();
    if (isExpired) {
      localStorage.removeItem("token");
      handleInit();
      return;
    }
    console.log("isExpired", isExpired);
    setLoginContext({
      userId: decoded.userId,
      authenticated: decoded.authenticated,
      email: decoded.email,
      phone: decoded.phone,
      exp: decoded.exp,
      role: decoded.role,
      fullName: decoded.fullName,
      street1: decoded.street1,
      street2: decoded.street2,
      city: decoded.city,
      state: decoded.state,
      zipCode: decoded.zipCode,
      firmName: decoded.firmName,
      billingEmail: decoded.billingEmail,
      dbaName: decoded.dbaName,
      cityId: decoded.cityId,
      stateId: decoded.stateId,
      countryId: decoded.countryId,
      connectedAccountId: decoded.connectedAccountId,
      preferredReporterId: decoded.preferredReporterId,
      liveLitigationLoginId: decoded.liveLitigationLoginId,
    });
    setAuth(true);
    handleInit();
  };

  const handleInit = () => {
    setTimeout(() => {
      setInit(false);
    }, 1000);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setAuth(false);
    setLoginContext({});
    setReady(false);
    handleInit();
    setActiveMenu("Home");
  };

  const handleGoHome = () => {
    window.location.reload();
  };

  const handleChangeMenu = (menuItem, newJob, jobId) => {
    console.log(menuItem, newJob);
    setActiveMenu(menuItem);
    setInitJobId(jobId);
    if (newJob) setInitNewJob(true);
  };

  return (
    <AuthContext.Provider value={{ loginContext, themeColor }}>
      <div>
        {authenticated &&
          !initializing &&
          isWelcome &&
          localStorage.getItem("stenexusWelcome") !== "false" &&
          loginContext.role !== "Admin" && (
            <Welcome setWelcome={setWelcome} isWelcome={isWelcome} />
          )}
        {authenticated && !initializing && (
          <MenuItems
            handleChangeMenu={handleChangeMenu}
            handleGoHome={handleGoHome}
            handleLogout={handleLogout}
            authenticated={authenticated}
            activeMenu={activeMenu}
          />
        )}
        {authenticated && !initializing && activeMenu === "Home" && (
          <Home handleChangeMenu={handleChangeMenu} />
        )}
        {authenticated && !initializing && activeMenu === "Users" && <Users />}
        {authenticated && !initializing && activeMenu === "Availability" && (
          <Availability />
        )}
        {authenticated && !initializing && activeMenu === "Jobs" && (
          <Jobs
            initNewJob={initNewJob}
            setInitNewJob={setInitNewJob}
            initJobId={initJobId}
            setInitJobId={setInitJobId}
          />
        )}
        {authenticated && !initializing && activeMenu === "Invite" && (
          <Invite />
        )}
        {authenticated && !initializing && activeMenu === "Profile" && (
          <Profile handleCreateLoginContext={handleCreateLoginContext} />
        )}

        {!authenticated && !initializing && (
          <Login
            email={email}
            phone={phone}
            onChange={handleChange}
            handleCreatePass={handleCreatePass}
            isReady={isReady}
            passcode={passcode}
            handleValidatePass={handleValidatePass}
            loading={loading}
            errorMessage={errorMessage}
            isRemember={isRemember}
            setRemember={setRemember}
            onChangePasscode={handleChangePasscode}
            isEmailLogin={isEmailLogin}
            setLoginMethod={setLoginMethod}
          />
        )}
        {initializing && (
          <Dimmer active>
            <Loader size="large">
              <Image src="./images/logo.png" style={{ width: "200px" }} />
            </Loader>
          </Dimmer>
        )}
      </div>
    </AuthContext.Provider>
  );
};
export default Dashboard;
